import { inject, Injectable } from '@angular/core';
import { map, switchMap } from 'rxjs/operators';
import { AdvertiserHttpService } from '../http-services/advertiser-http.service';
import { PublisherShopHttpService } from '../http-services/publisher-shop-http.service';
import { ICreateAccount, ICreateAccountRaw } from '../shared/account/create-account.helper';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { Account } from '../shared/account/account.types';
import AdvertiserWithStats = Account.AdvertiserWithStats;

@Injectable({
  providedIn: 'root',
})
export class AccountsService {
  public publisherWithEmptyStats$: Observable<Account.PublisherWithStats[]>;
  public publishersWithStatsLoading$ = new BehaviorSubject<boolean>(false);
  public advertisersWithEmptyStats$: Observable<AdvertiserWithStats[]>;
  public advertisersWithStatsLoading$ = new BehaviorSubject<boolean>(false);

  private _accountsLoaded$ = new BehaviorSubject<boolean>(false);
  private _publishers = new ReplaySubject<ICreateAccount[]>(1);
  private _advertisers = new ReplaySubject<ICreateAccount[]>(1);

  private readonly advertiserHttpService: AdvertiserHttpService;
  private readonly publisherShopHttpService: PublisherShopHttpService;

  constructor() {
    this.advertiserHttpService = inject(AdvertiserHttpService);
    this.publisherShopHttpService = inject(PublisherShopHttpService);
  }

  public get areAccountsLoaded$(): Observable<boolean> {
    return this._accountsLoaded$.asObservable();
  }

  public get accounts$(): Observable<[ICreateAccount[], ICreateAccount[]]> {
    return combineLatest([ this.publishers$, this.advertisers$ ]);
  }

  public get publishers$(): Observable<ICreateAccount[]> {
    return this._publishers.asObservable()
  }

  public get advertisers$(): Observable<ICreateAccount[]> {
    return this._advertisers.asObservable()
  }

  public terminateAccountsLoading() {
    this._accountsLoaded$.next(true);
  }

  public init() {
    this.fetchPublishers();
    this.fetchAdvertisers();

    combineLatest([
      this._publishers,
      this._advertisers
    ])
      .subscribe(
        () => this._accountsLoaded$.next(true),
        () => this._accountsLoaded$.next(true)
      );

    this.publishersWithStatsLoading$.next(true);

    this.publisherWithEmptyStats$ = this.publishers$
      .pipe(
        switchMap(list => {
          const accounts =  Object.values(this.getAccountsMap(list));
          const value = accounts.map((account: any) => ({ account, stats: {} }));
          return of(value as Account.PublisherWithStats[]);
        })
      );

    this.advertisersWithStatsLoading$.next(true);

    this.advertisersWithEmptyStats$ = this.advertisers$
      .pipe(
        switchMap(list => {
          const accounts =  Object.values(this.getAccountsMap(list));
          const value = accounts.map((account: any) => ({ account, stats: {} }));
          return of(value as Account.AdvertiserWithStats[]);
        })
      );
  }

  public fetchPublishers() {
    return this.publisherShopHttpService.getShops({ page: 1, pageSize: 4000 })
      .pipe(
        map(this.addAccountTypeToList(Account.Type.PUBLISHER)),
      )
      .subscribe(
        list => this._publishers.next(list),
        () => this._publishers.next([])
      );
  }

  public fetchAdvertisers() {
    return this.advertiserHttpService.getAdvertisers({ page: 1, pageSize: 10_000 })
      .pipe(
        map(this.addAccountTypeToList(Account.Type.ADVERTISER)),
      )
      .subscribe(
        list => this._advertisers.next(list),
        () => this._advertisers.next([])
      );
  }

  private addAccountTypeToList(accountType: Account.Type) {
    return (accounts: ICreateAccountRaw[]) => accounts.map(account => ({ ...account, accountType }));
  }

  private getAccountsMap(accounts: ICreateAccount[]): Record<ICreateAccount['id'], ICreateAccount> {
    return accounts.reduce((acc, curr) => {
      acc[curr.id] = curr;
      return acc;
    }, {} as Record<string, ICreateAccount>);
  }

}
